/**
 * BetaPulseModal · Feb 2026
 * ───────────────────────────────────────────────────────────────────
 * The full Beta Pulse — Soulumo's 9-metric check-in. Shown once per
 * tester after ≥2 completed sessions AND ≥24h since first use.
 *
 * Flow:
 *   Step 1 · Settle screen (gentle intro, 1 Continue tap)
 *   Step 2 · Nine 0–10 metrics, one per screen (fast, no scroll)
 *   Step 3 · Adaptive follow-up by overall score:
 *              0–5  → "What got in the way?" (chips) + optional free-text
 *              6–7  → "What would make this an 8+?" free-text
 *              8–10 → Testimonial invitation
 *   Step 4 · (8-10 path) Testimonial permission (name / anon / team-only)
 *   Step 5 · Thank-you
 *
 * UX rules (Michael):
 *   · One decision per screen · No clinical survey feel · Soulumo
 *     orb present on settle screen · Spacious, premium type ·
 *     "Not now" is always visible — never trap the user.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import InstallOrb from "./InstallOrb";

const API = process.env.REACT_APP_BACKEND_URL || "";
const TOK = {
  cream: "#f5ecd9",
  creamDim: "rgba(245,236,217,0.72)",
  creamMist: "rgba(245,236,217,0.44)",
  gold: "#e0a97a",
  goldSoft: "rgba(224,169,122,0.86)",
  goldMist: "rgba(224,169,122,0.22)",
};

function auth() {
  const t = localStorage.getItem("reclaim_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function logOutcome(prompt_type, outcome) {
  try {
    await fetch(`${API}/api/beta-feedback/prompt-log`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...auth() },
      body: JSON.stringify({ prompt_type, outcome }),
    });
  } catch (_) {}
}

const inputStyle = {
  width: "100%",
  background: "rgba(0,0,0,0.35)",
  border: `1px solid ${TOK.goldMist}`,
  color: TOK.cream,
  borderRadius: 12,
  padding: "12px 14px",
  fontFamily: '"Manrope", sans-serif',
  fontSize: "0.95rem",
  outline: "none",
  resize: "vertical",
};

export default function BetaPulseModal({ open, onClose, context = {}, source = "post-install" }) {
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(0); // 0 settle, 1..9 metrics, 10 adaptive, 11 testimonial, 12 permission, 13 done
  const [scores, setScores] = useState({});
  const [blockers, setBlockers] = useState([]);
  const [freeChange, setFreeChange] = useState("");
  const [improve, setImprove] = useState("");
  const [differentiationComment, setDifferentiationComment] = useState("");
  const [testimonialText, setTestimonialText] = useState("");
  const [permission, setPermission] = useState(null);
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const dialogRef = useRef(null);

  // Load config once + lock body scroll
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (!config) {
      fetch(`${API}/api/beta-feedback/config`)
        .then((r) => r.ok ? r.json() : null)
        .then((d) => d && setConfig(d))
        .catch(() => {});
    }
    return () => { document.body.style.overflow = prev; };
  }, [open, config]);

  // Reset state when modal reopens
  useEffect(() => {
    if (open) {
      setStep(0);
      setScores({});
      setBlockers([]);
      setFreeChange("");
      setImprove("");
      setDifferentiationComment("");
      setTestimonialText("");
      setPermission(null);
      setErr("");
    }
  }, [open]);

  if (!open) return null;

  const metrics = (config && config.pulse_metrics) || [];
  const totalMetricSteps = metrics.length;
  const overall = scores.overall_experience;
  const diffScore = scores.differentiation;
  const isPromoter = typeof overall === "number" && overall >= 8;
  const isPassive  = typeof overall === "number" && overall >= 6 && overall <= 7;
  const isDetractor = typeof overall === "number" && overall <= 5;

  // Navigation
  const goMetric = (i) => setStep(1 + i);
  const goAdaptive = () => setStep(1 + totalMetricSteps);          // 10
  const goTestimonial = () => setStep(1 + totalMetricSteps + 1);   // 11
  const goPermission = () => setStep(1 + totalMetricSteps + 2);    // 12
  const goDone = () => setStep(1 + totalMetricSteps + 3);          // 13

  const handleScore = (metricKey, value) => {
    setScores((s) => ({ ...s, [metricKey]: value }));
    const idx = metrics.findIndex((m) => m.key === metricKey);
    if (idx < 0) return;
    if (idx + 1 < metrics.length) {
      setTimeout(() => goMetric(idx + 1), 260);
    } else {
      setTimeout(() => goAdaptive(), 260);
    }
  };

  const toggleBlocker = (b) => {
    setBlockers((cur) => (cur.includes(b) ? cur.filter((x) => x !== b) : [...cur, b]));
  };

  const submit = async ({ withTestimonial = false, withPermission = null } = {}) => {
    setErr("");
    setSubmitting(true);
    try {
      const payload = {
        type: "pulse",
        scores: scores,
        answers: {
          blockers: isDetractor && blockers.length ? blockers : null,
          free_change: isDetractor && freeChange.trim() ? freeChange.trim() : null,
          improvement_to_8_plus: isPassive && improve.trim() ? improve.trim() : null,
          differentiation_comment: (typeof diffScore === "number" && diffScore >= 8 && differentiationComment.trim())
            ? differentiationComment.trim() : null,
        },
        testimonial: withTestimonial && testimonialText.trim() ? {
          text: testimonialText.trim(),
          permission: withPermission,
          approved: false,
        } : null,
        context: {
          installation_id: context.journeyId || context.installationId || null,
          installation_name: context.installationName || null,
          category: context.category || null,
          experience_type: context.experienceType || null,
          duration_sec: context.durationSec || null,
          listened_sec: context.listenedSec || null,
          completion_pct: context.completionPct || null,
          voice_id: context.voiceId || null,
          voice_label: context.voiceLabel || null,
          own_voice: context.ownVoice || null,
          music: context.music || null,
          day_or_night: context.dayOrNight || null,
          trigger_source: source,
          device: window.innerWidth < 768 ? "mobile" : "desktop",
        },
      };
      const r = await fetch(`${API}/api/beta-feedback/events`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      logOutcome("pulse", "submitted");
      try { localStorage.setItem("soulumo_pulse_given", "1"); } catch (_) {}
    } catch (e) {
      setErr(e.message || "Could not send — please try again.");
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
    goDone();
  };

  const dismiss = () => {
    logOutcome("pulse", "dismissed");
    onClose();
  };

  // ── Render ────────────────────────────────────────────────────
  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center p-0 sm:p-5"
      style={{ background: "rgba(0,0,0,0.86)", backdropFilter: "blur(10px)" }}
      onClick={dismiss}
      role="dialog"
      aria-modal="true"
      data-testid="beta-pulse-modal"
    >
      <div
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md sm:rounded-3xl p-6 sm:p-8 flex flex-col"
        style={{
          background: "linear-gradient(180deg, rgba(10,20,40,0.98) 0%, rgba(5,11,26,1) 100%)",
          border: `1px solid ${TOK.goldMist}`,
          color: TOK.cream,
          maxHeight: "100vh",
          overflowY: "auto",
          boxShadow: "0 30px 90px rgba(0,0,0,0.6), 0 0 60px rgba(224,169,122,0.08)",
        }}
      >
        <div
          className="uppercase mb-2"
          style={{
            color: TOK.goldSoft,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.58rem",
            letterSpacing: "0.36em",
          }}
        >
          ✦ Soulumo · a quiet check-in ✦
        </div>

        {/* Step 0 — settle */}
        {step === 0 && (
          <div data-testid="pulse-step-settle" className="py-4">
            <div className="flex justify-center my-4" aria-hidden>
              <InstallOrb size={120} />
            </div>
            <h2
              className="italic font-light text-center"
              style={{
                color: TOK.cream,
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "clamp(1.4rem, 5vw, 1.8rem)",
                lineHeight: 1.3,
              }}
            >
              If you have a moment, Michael would love your honest reflection on Soulumo so far.
            </h2>
            <p
              className="mt-4 italic text-center"
              style={{
                color: TOK.creamMist,
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "1rem",
                lineHeight: 1.65,
              }}
            >
              A series of quick taps — no typing unless you want to.
              It takes about a minute.
            </p>
            <button
              type="button"
              onClick={() => goMetric(0)}
              data-testid="pulse-start"
              className="mt-6 w-full rounded-full py-3"
              style={{
                background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
                border: "1px solid #f5e0c5",
                color: "#1a0f05",
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.78rem",
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                fontWeight: 700,
                minHeight: 48,
                cursor: "pointer",
              }}
            >
              Begin
            </button>
            <button
              type="button"
              onClick={dismiss}
              data-testid="pulse-not-now"
              className="mt-2 w-full py-2 italic"
              style={{
                background: "transparent",
                border: "none",
                color: TOK.creamMist,
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "0.9rem",
                cursor: "pointer",
              }}
            >
              Not now
            </button>
          </div>
        )}

        {/* Steps 1..N — metrics */}
        {step >= 1 && step <= totalMetricSteps && metrics.length > 0 && (
          <MetricStep
            index={step - 1}
            total={totalMetricSteps}
            metric={metrics[step - 1]}
            value={scores[metrics[step - 1].key]}
            onScore={(v) => handleScore(metrics[step - 1].key, v)}
            onBack={step > 1 ? () => setStep((s) => s - 1) : null}
            onDismiss={dismiss}
          />
        )}

        {/* Step N+1 — adaptive follow-up */}
        {step === 1 + totalMetricSteps && (
          <AdaptiveStep
            overall={overall}
            config={config}
            blockers={blockers}
            toggleBlocker={toggleBlocker}
            freeChange={freeChange}
            setFreeChange={setFreeChange}
            improve={improve}
            setImprove={setImprove}
            diffScore={diffScore}
            differentiationComment={differentiationComment}
            setDifferentiationComment={setDifferentiationComment}
            onBack={() => setStep(totalMetricSteps)}
            onNext={() => {
              if (isPromoter) goTestimonial();
              else submit({ withTestimonial: false });
            }}
            onDismiss={dismiss}
            err={err}
            submitting={submitting}
          />
        )}

        {/* Step N+2 — testimonial */}
        {step === 1 + totalMetricSteps + 1 && (
          <TestimonialStep
            config={config}
            testimonialText={testimonialText}
            setTestimonialText={setTestimonialText}
            onSkip={() => submit({ withTestimonial: false })}
            onNext={() => {
              if (testimonialText.trim()) goPermission();
              else submit({ withTestimonial: false });
            }}
            onDismiss={dismiss}
            err={err}
            submitting={submitting}
          />
        )}

        {/* Step N+3 — permission */}
        {step === 1 + totalMetricSteps + 2 && (
          <PermissionStep
            config={config}
            permission={permission}
            setPermission={setPermission}
            onSubmit={() => submit({ withTestimonial: true, withPermission: permission })}
            onDismiss={() => submit({ withTestimonial: true, withPermission: "team_only" })}
            err={err}
            submitting={submitting}
          />
        )}

        {/* Step N+4 — done */}
        {step === 1 + totalMetricSteps + 3 && (
          <DoneStep onClose={onClose} />
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
function MetricStep({ index, total, metric, value, onScore, onBack, onDismiss }) {
  return (
    <div data-testid={`pulse-metric-${metric.key}`} className="py-4">
      <div
        className="flex items-center justify-between text-xs"
        style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif', letterSpacing: "0.18em" }}
      >
        <span>{String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}</span>
        <button
          type="button"
          onClick={onDismiss}
          className="italic"
          style={{ color: TOK.creamMist, fontFamily: '"Cormorant Garamond", serif', fontSize: "0.86rem" }}
        >
          Not now
        </button>
      </div>
      <h2
        className="italic font-light mt-4"
        style={{
          color: TOK.cream,
          fontFamily: '"Cormorant Garamond", serif',
          fontSize: "clamp(1.35rem, 4.8vw, 1.65rem)",
          lineHeight: 1.3,
        }}
      >
        {metric.prompt}
      </h2>
      <p
        className="mt-2 italic"
        style={{
          color: TOK.creamMist,
          fontFamily: '"Cormorant Garamond", serif',
          fontSize: "0.9rem",
        }}
      >
        {rangeHint(metric.key)}
      </p>
      <div className="mt-6 grid grid-cols-11 gap-1" data-testid={`pulse-grid-${metric.key}`}>
        {Array.from({ length: 11 }, (_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onScore(i)}
            data-testid={`pulse-score-${metric.key}-${i}`}
            style={{
              minHeight: 44,
              padding: "6px 0",
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.95rem",
              fontWeight: 600,
              borderRadius: 8,
              background: value === i
                ? "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)"
                : "rgba(255,255,255,0.04)",
              color: value === i ? "#1a0f05" : TOK.creamDim,
              border: `1px solid ${value === i ? "#f5e0c5" : TOK.goldMist}`,
              cursor: "pointer",
              transition: "all 160ms ease",
            }}
          >
            {i}
          </button>
        ))}
      </div>
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="mt-5 py-1 italic"
          style={{
            background: "transparent",
            border: "none",
            color: TOK.creamMist,
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "0.9rem",
          }}
        >
          ← Back
        </button>
      )}
    </div>
  );
}

function rangeHint(key) {
  const hints = {
    overall_experience: "0 = very poor · 10 = exceptional",
    emotional_shift:    "0 = no difference · 10 = a significant positive shift",
    personal_relevance: "0 = not at all · 10 = completely",
    immersion:          "0 = not immersive · 10 = exceptional",
    voice:              "0 = poor · 10 = exceptional",
    ease:               "0 = difficult · 10 = effortless",
    return_intent:      "0 = very unlikely · 10 = extremely likely",
    recommendation:     "0 = not likely · 10 = extremely likely",
    differentiation:    "0 = not different · 10 = completely different",
  };
  return hints[key] || "0 — 10";
}

// ──────────────────────────────────────────────────────────────────
function AdaptiveStep({
  overall, config, blockers, toggleBlocker, freeChange, setFreeChange,
  improve, setImprove, diffScore, differentiationComment, setDifferentiationComment,
  onBack, onNext, onDismiss, err, submitting,
}) {
  const isDetractor = typeof overall === "number" && overall <= 5;
  const isPassive = typeof overall === "number" && overall >= 6 && overall <= 7;
  const isPromoter = typeof overall === "number" && overall >= 8;
  const showDiff = typeof diffScore === "number" && diffScore >= 8;
  const options = (config && config.blockers) || [];

  return (
    <div data-testid="pulse-adaptive" className="py-4">
      <div
        className="flex items-center justify-end"
        style={{ color: TOK.creamMist, fontSize: "0.8rem" }}
      >
        <button type="button" onClick={onDismiss} className="italic"
          style={{ color: TOK.creamMist, fontFamily: '"Cormorant Garamond", serif' }}>
          Not now
        </button>
      </div>

      {isDetractor && (
        <>
          <h2
            className="italic font-light mt-2"
            style={{
              color: TOK.cream,
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: "clamp(1.3rem, 4.8vw, 1.6rem)",
              lineHeight: 1.3,
            }}
          >
            What got in the way?
          </h2>
          <p className="mt-1 italic" style={{ color: TOK.creamMist, fontFamily: '"Cormorant Garamond", serif' }}>
            Tap anything that applies — more than one is fine.
          </p>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="pulse-blockers">
            {options.map((b) => {
              const on = blockers.includes(b);
              return (
                <button
                  key={b}
                  type="button"
                  onClick={() => toggleBlocker(b)}
                  data-testid={`pulse-blocker-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  style={{
                    padding: "7px 14px",
                    borderRadius: 999,
                    border: `1px solid ${on ? "#f5e0c5" : TOK.goldMist}`,
                    background: on ? "linear-gradient(180deg,#f0c790,#d99b6b)" : "rgba(255,255,255,0.04)",
                    color: on ? "#1a0f05" : TOK.creamDim,
                    fontFamily: '"Manrope", sans-serif',
                    fontSize: "0.82rem",
                    fontWeight: on ? 600 : 500,
                    cursor: "pointer",
                  }}
                >
                  {b}
                </button>
              );
            })}
          </div>
          <label
            className="block mt-5 uppercase"
            style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif', fontSize: "0.6rem", letterSpacing: "0.28em" }}
          >
            If we changed one thing, what should it be? (optional)
          </label>
          <textarea
            value={freeChange}
            onChange={(e) => setFreeChange(e.target.value)}
            rows={2}
            data-testid="pulse-free-change"
            style={{ ...inputStyle, marginTop: 6 }}
          />
        </>
      )}

      {isPassive && (
        <>
          <h2
            className="italic font-light mt-2"
            style={{
              color: TOK.cream,
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: "clamp(1.3rem, 4.8vw, 1.6rem)",
              lineHeight: 1.3,
            }}
          >
            What would have made this an 8, 9 or 10 for you?
          </h2>
          <p className="mt-1 italic" style={{ color: TOK.creamMist, fontFamily: '"Cormorant Garamond", serif' }}>
            Specifics help us more than you know.
          </p>
          <textarea
            value={improve}
            onChange={(e) => setImprove(e.target.value)}
            rows={4}
            autoFocus
            data-testid="pulse-improve"
            style={{ ...inputStyle, marginTop: 14 }}
          />
        </>
      )}

      {isPromoter && (
        <>
          <h2
            className="italic font-light mt-2"
            style={{
              color: TOK.cream,
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: "clamp(1.3rem, 4.8vw, 1.6rem)",
              lineHeight: 1.3,
            }}
          >
            Thank you — this means a great deal.
          </h2>
          <p className="mt-1 italic" style={{ color: TOK.creamMist, fontFamily: '"Cormorant Garamond", serif' }}>
            One more quick thing after this.
          </p>
        </>
      )}

      {showDiff && (
        <div className="mt-5 pt-4" style={{ borderTop: `1px dashed ${TOK.goldMist}` }}>
          <label
            className="block uppercase"
            style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif', fontSize: "0.6rem", letterSpacing: "0.28em" }}
          >
            ✦ {(config && config.differentiation_followup) || "What felt different?"}
          </label>
          <textarea
            value={differentiationComment}
            onChange={(e) => setDifferentiationComment(e.target.value)}
            rows={3}
            data-testid="pulse-differentiation-comment"
            style={{ ...inputStyle, marginTop: 6 }}
          />
        </div>
      )}

      {err && (
        <div className="mt-3 text-sm italic" style={{ color: "#f0a0a0", fontFamily: '"Cormorant Garamond", serif' }}>
          {err}
        </div>
      )}

      <button
        type="button"
        onClick={onNext}
        disabled={submitting}
        data-testid="pulse-adaptive-next"
        className="mt-6 w-full rounded-full py-3 disabled:opacity-60"
        style={{
          background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
          border: "1px solid #f5e0c5",
          color: "#1a0f05",
          fontFamily: '"Manrope", sans-serif',
          fontSize: "0.78rem",
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          fontWeight: 700,
          minHeight: 48,
          cursor: submitting ? "wait" : "pointer",
        }}
      >
        {submitting ? "Sending…" : (isPromoter ? "Continue" : "Send")}
      </button>
      <button
        type="button"
        onClick={onBack}
        className="mt-2 py-1 italic"
        style={{ background: "transparent", border: "none", color: TOK.creamMist,
          fontFamily: '"Cormorant Garamond", serif', fontSize: "0.9rem" }}
      >
        ← Back
      </button>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
function TestimonialStep({ config, testimonialText, setTestimonialText, onSkip, onNext, onDismiss, err, submitting }) {
  const copy = (config && config.testimonial_prompt) || {};
  return (
    <div data-testid="pulse-testimonial" className="py-4">
      <h2
        className="italic font-light"
        style={{
          color: TOK.cream,
          fontFamily: '"Cormorant Garamond", serif',
          fontSize: "clamp(1.35rem, 4.8vw, 1.7rem)",
          lineHeight: 1.3,
        }}
      >
        {copy.intro || "We're so glad this landed for you."}
      </h2>
      <p className="mt-2 italic" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif', fontSize: "1rem", lineHeight: 1.6 }}>
        {copy.question || "Would you tell us, in your own words, what Soulumo did for you?"}
      </p>
      <p className="mt-1" style={{ color: TOK.creamMist, fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic", fontSize: "0.9rem" }}>
        {copy.sub || "What changed in how you felt — even slightly?"}
      </p>
      <textarea
        value={testimonialText}
        onChange={(e) => setTestimonialText(e.target.value)}
        rows={5}
        autoFocus
        data-testid="pulse-testimonial-text"
        style={{ ...inputStyle, marginTop: 14 }}
      />
      {err && (
        <div className="mt-3 text-sm italic" style={{ color: "#f0a0a0", fontFamily: '"Cormorant Garamond", serif' }}>
          {err}
        </div>
      )}
      <button
        type="button"
        onClick={onNext}
        disabled={submitting}
        data-testid="pulse-testimonial-share"
        className="mt-5 w-full rounded-full py-3 disabled:opacity-60"
        style={{
          background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
          border: "1px solid #f5e0c5",
          color: "#1a0f05",
          fontFamily: '"Manrope", sans-serif',
          fontSize: "0.78rem",
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          fontWeight: 700,
          minHeight: 48,
          cursor: submitting ? "wait" : "pointer",
        }}
      >
        {submitting ? "Sending…" : (copy.cta || "Share my experience")}
      </button>
      <button
        type="button"
        onClick={onSkip}
        data-testid="pulse-testimonial-skip"
        className="mt-2 w-full py-2 italic"
        style={{ background: "transparent", border: "none", color: TOK.creamMist,
          fontFamily: '"Cormorant Garamond", serif', fontSize: "0.9rem" }}
      >
        {copy.secondary || "Not now"}
      </button>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
function PermissionStep({ config, permission, setPermission, onSubmit, onDismiss, err, submitting }) {
  const copy = (config && config.permission_prompt) || {};
  const options = copy.options || [
    { value: "name", label: "Yes, with my first name" },
    { value: "anonymous", label: "Yes, anonymously" },
    { value: "team_only", label: "No, just for the Soulumo team" },
  ];
  return (
    <div data-testid="pulse-permission" className="py-4">
      <h2
        className="italic font-light"
        style={{
          color: TOK.cream,
          fontFamily: '"Cormorant Garamond", serif',
          fontSize: "clamp(1.3rem, 4.8vw, 1.6rem)",
          lineHeight: 1.3,
        }}
      >
        {copy.question || "Would you be happy for us to share your words to help others understand Soulumo?"}
      </h2>
      <div className="mt-5 space-y-2.5">
        {options.map((opt) => {
          const on = permission === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => setPermission(opt.value)}
              data-testid={`pulse-permission-${opt.value}`}
              className="w-full text-left rounded-xl px-4 py-3"
              style={{
                background: on ? "linear-gradient(180deg,#f0c790,#d99b6b)" : "rgba(255,255,255,0.04)",
                border: `1px solid ${on ? "#f5e0c5" : TOK.goldMist}`,
                color: on ? "#1a0f05" : TOK.cream,
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.95rem",
                fontWeight: on ? 600 : 500,
                cursor: "pointer",
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      {err && (
        <div className="mt-3 text-sm italic" style={{ color: "#f0a0a0", fontFamily: '"Cormorant Garamond", serif' }}>
          {err}
        </div>
      )}
      <button
        type="button"
        onClick={onSubmit}
        disabled={!permission || submitting}
        data-testid="pulse-permission-submit"
        className="mt-5 w-full rounded-full py-3 disabled:opacity-60"
        style={{
          background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
          border: "1px solid #f5e0c5",
          color: "#1a0f05",
          fontFamily: '"Manrope", sans-serif',
          fontSize: "0.78rem",
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          fontWeight: 700,
          minHeight: 48,
          cursor: !permission || submitting ? "not-allowed" : "pointer",
        }}
      >
        {submitting ? "Sending…" : "Send"}
      </button>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
function DoneStep({ onClose }) {
  return (
    <div data-testid="pulse-done" className="py-6">
      <h2
        className="italic font-light"
        style={{
          color: TOK.cream,
          fontFamily: '"Cormorant Garamond", serif',
          fontSize: "clamp(1.5rem, 5vw, 1.9rem)",
          lineHeight: 1.25,
        }}
      >
        Thank you — truly.
      </h2>
      <p className="mt-4 italic" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif', fontSize: "1rem", lineHeight: 1.6 }}>
        Your reflections quietly shape what Soulumo becomes next.
        Michael reads every one personally.
      </p>
      <button
        type="button"
        onClick={onClose}
        data-testid="pulse-done-close"
        className="mt-6 w-full rounded-full py-3"
        style={{
          background: "transparent",
          border: `1px solid ${TOK.gold}`,
          color: TOK.gold,
          fontFamily: '"Manrope", sans-serif',
          fontSize: "0.76rem",
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          minHeight: 44,
          cursor: "pointer",
        }}
      >
        Close
      </button>
    </div>
  );
}
